import type {
  UseInfiniteQueryReturnType,
  InfiniteData,
} from '@tanstack/vue-query'
import type { PaginationState } from '@tanstack/vue-table'
import { toNumber } from '@/utils/number.js'
import type { QueryPaginationMeta } from '@/utils/jsonapi.js'

export const useInfiniteScrollPaginator = <
  TQueryResponse extends {
    meta?: { pagination?: QueryPaginationMeta }
  },
>(
  pagination: Ref<PaginationState>,
) => {
  const firstPageParam = pagination.value.pageIndex
  const firstPageParamNotFirstPage = firstPageParam > 0

  const enablePaginationWatchers = (
    query: Partial<
      UseInfiniteQueryReturnType<InfiniteData<TQueryResponse, unknown>, Error>
    >,
  ) => {
    const lastPageIndex = computed(() => {
      const pages = query.data?.value?.pages
      const lastPage = pages?.[pages.length - 1]
      return lastPage?.meta?.pagination?.page
    })

    watch(
      lastPageIndex,
      async (newValue) => {
        const queryCurrentPage = toNumber(newValue)
        const pageState = pagination.value.pageIndex + 1

        if (queryCurrentPage < pageState) {
          await query.fetchNextPage?.()
        }
      },
      { immediate: true },
    )

    watch(
      () => pagination.value.pageIndex,
      async (newValue) => {
        // Get last request because this one was called with correct pageSize
        // and therefore should have the correct information on the totalPages
        const lastRequest =
          query.data?.value?.pages?.[query.data?.value?.pages?.length - 1]
        const totalPages = lastRequest?.meta?.pagination?.pages

        if (
          newValue > 0 &&
          query.hasNextPage &&
          newValue <= toNumber(totalPages)
        ) {
          await query.fetchNextPage?.()
        } else {
          await query.refetch?.()
        }
      },
    )
  }

  return {
    enablePaginationWatchers,
    initialPageParam: firstPageParamNotFirstPage
      ? // If initial page param is not the first page, start from the first page but increase `pageSize` to get all previous pages
        {
          pageIndex: 1,
          pageSize: (firstPageParam + 1) * pagination.value.pageSize,
        }
      : // Otherwise, do normal behaviour of starting from the first page
        {
          pageIndex: pagination.value.pageIndex || 1, // Start from 1 if not defined
          pageSize: pagination.value.pageSize, // Normal behavior when starting from page 1
        },

    getNextPageParam: (lastPage: TQueryResponse) => {
      if (!lastPage || !lastPage.meta?.pagination) {
        return null
      }

      const currentPage = toNumber(lastPage.meta.pagination.page)
      const totalPages = toNumber(lastPage.meta.pagination.pages)

      // Stop fetching if all pages are loaded
      if (currentPage >= totalPages) {
        return null
      }

      const nextPage =
        currentPage < firstPageParam
          ? // If first page param is not the first page, we need to increment the page with that value
            currentPage + firstPageParam
          : // Otherwise just increment the page by 1
            currentPage + 1

      return {
        pageIndex: nextPage,
        pageSize: pagination.value.pageSize,
      }
    },
  }
}
